import { apiClient } from './client';

/**
 * Phase 7 — клиент сервер-state sync API.
 *
 * Используется в двух сценариях:
 *  1. На загрузке/реконнекте получаем «курсоры» (последний известный
 *     сервером seq на каждый канал/DM) и сравниваем с тем, что у нас в
 *     локальном сторе.
 *  2. Если видим дырку (server seq > local seq), запрашиваем
 *     `syncChannel` / `syncDm` с `afterSeq = local`, и применяем
 *     события до полного догона.
 *
 * Ключи seq передаются как строка, потому что серверные BigInt'ы не
 * влезают в Number. Сравнение всегда через `BigInt(a) > BigInt(b)`.
 */

export type SyncEventType =
  | 'message.created'
  | 'message.edited'
  | 'message.deleted'
  | 'message.pinned'
  | 'message.unpinned'
  | 'reaction.changed'
  | 'read.advanced';

export interface SyncEvent {
  id: string;
  seq: string;
  type: SyncEventType;
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface SyncPage {
  events: SyncEvent[];
  fromSeq: string;
  nextSeq: string;
  hasMore: boolean;
}

export interface SyncCursors {
  channels: Array<{ channelId: string; lastSeq: string }>;
  dms: Array<{ peerId: string; lastSeq: string }>;
}

export const syncApi = {
  async getCursors(): Promise<SyncCursors> {
    const res = await apiClient.get<SyncCursors>('/api/sync/cursors');
    return res.data;
  },

  async syncChannel(
    channelId: string,
    afterSeq: string,
    limit?: number,
  ): Promise<SyncPage> {
    const res = await apiClient.get<SyncPage>(`/api/sync/channel/${channelId}`, {
      params: { afterSeq, limit },
    });
    return res.data;
  },

  async syncDm(peerId: string, afterSeq: string, limit?: number): Promise<SyncPage> {
    const res = await apiClient.get<SyncPage>(`/api/sync/dm/${peerId}`, {
      params: { afterSeq, limit },
    });
    return res.data;
  },
};

/**
 * Безопасное сравнение строковых seq как BigInt. Возвращает true, если
 * `a > b`. Невалидные строки трактуются как 0.
 */
export function seqGt(a: string | undefined | null, b: string | undefined | null): boolean {
  try {
    return BigInt(a ?? '0') > BigInt(b ?? '0');
  } catch {
    return false;
  }
}

/** Возвращает максимум двух seq-строк (по BigInt). */
export function seqMax(a: string | undefined | null, b: string | undefined | null): string {
  return seqGt(a, b) ? (a ?? '0') : (b ?? '0');
}
